import React, { useMemo, useState } from "react";
import {
  useAddBlockedEmailDomainMutation,
  useDeleteBlockedEmailDomainMutation,
  useGetBlockedEmailDomainsQuery,
} from "@/store/admin_api";
import { BlockedEmailDomain } from "@/model/interface/BlockedEmailDomain";
import { apiErrorMessage } from "@/util/apiError";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { FaBan, FaPlus, FaTrash } from "react-icons/fa";
import { MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function EmailBlocklistPage() {
  const { data: domains, isLoading } = useGetBlockedEmailDomainsQuery();
  const [addDomain, { isLoading: isAdding }] = useAddBlockedEmailDomainMutation();
  const [deleteDomain] = useDeleteBlockedEmailDomainMutation();
  const [newDomain, setNewDomain] = useState("");
  const [newNote, setNewNote] = useState("");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!domains) {
      return [];
    }
    const query = search.trim().toLowerCase();
    if (!query) {
      return domains;
    }
    return domains.filter((entry) => entry.domain.toLowerCase().includes(query));
  }, [domains, search]);

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const domain = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!domain) {
      return;
    }
    try {
      await addDomain({ domain, note: newNote.trim() || undefined }).unwrap();
      toast.success(`${domain} added to the blocklist`);
      setNewDomain("");
      setNewNote("");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to add the domain"));
    }
  };

  const handleDelete = async (entry: BlockedEmailDomain) => {
    setBusyId(entry.id);
    try {
      await deleteDomain(entry.id).unwrap();
      toast.success(`${entry.domain} removed from the blocklist`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to remove the domain"));
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Email Blocklist</h2>
        <p className="text-muted-foreground text-sm">
          Domains here are rejected at signup and on the waitlist. Seeded with known disposable providers; add your own.
        </p>
      </div>

      <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Domain</label>
          <Input
            value={newDomain}
            onChange={(event) => setNewDomain(event.target.value)}
            placeholder="10minutemail.com"
            className="w-64"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Note (optional)</label>
          <Input
            value={newNote}
            onChange={(event) => setNewNote(event.target.value)}
            placeholder="Why is this blocked?"
            className="w-72"
          />
        </div>
        <Button type="submit" disabled={isAdding || !newDomain.trim()}>
          <FaPlus className="mr-1 h-3 w-3" />
          Add
        </Button>
      </form>

      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search domains"
        className="max-w-sm"
      />

      <div className="text-sm text-muted-foreground">{filtered.length} domain(s)</div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Domain</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Added</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!filtered.length && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                No blocked domains
              </TableCell>
            </TableRow>
          )}
          {filtered.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="font-medium">
                <span className="inline-flex items-center gap-2">
                  <FaBan className="h-3 w-3 text-muted-foreground" />
                  {entry.domain}
                </span>
              </TableCell>
              <TableCell>
                <Badge variant={entry.source === "MANUAL" ? "default" : "secondary"}>{entry.source}</Badge>
              </TableCell>
              <TableCell className="max-w-xs truncate" title={entry.note}>
                {entry.note || "-"}
              </TableCell>
              <TableCell>
                {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : "-"}
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" disabled={busyId === entry.id} title="Options">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(entry)}>
                      <FaTrash className="mr-2 h-3 w-3" />
                      Remove entry
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
